import { Select, Trans } from '@lingui/react/macro';
import { Box, DialogContentText } from '@mui/material';
import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Button } from 'ui-kit';
import { BaseDialog, type DialogKey, useDialogStore } from '@/components/dialogs';
import { TokenInputField } from '@/components/input_field/token_input_field.tsx';
import { SliderWithInput } from '@/components/slider/slider_with_input';
import { useSharedTradingStore } from '@/feature/order_book/SharedTradingStore.ts';
import { useTokenFormatter } from '@/hooks/useTokenFormatter';
import { decimalStringToBigInt, type TokenNumberFormatOptions } from '@/utils/tokenNumberUtils.ts';
import { useIsOneClickEnabled } from '../../../hooks/useIsOneClickEnabled';
import { useTokenAmountValidation } from '../../../hooks/useTokenAmountValidation.ts';
import useAppStore, { useAccountBalance } from '../../../store/rootStore.ts';
import { useAddMarginOnChain } from '../hooks/useAddMarginOnChain.ts';
import { usePositionAPI } from '../hooks/usePositionAPI.ts';
import { MarginInfoRows } from './MarginInfoRows';
import { useMarginAmountSlider } from './useMarginAmountSlider';
import { useMarginProjection } from './useMarginProjection';

const DIALOG_KEY: DialogKey = 'adjustMarginDialog';
const DISPLAY_OPTIONS: Partial<TokenNumberFormatOptions> = { minDecimals: 0 };

function AdjustMarginDialogUI({
  addMargin,
  isPending,
  isSuccess,
  resetProcess,
}: {
  addMargin: (amount: bigint, marketId: number) => void;
  isPending: boolean;
  isSuccess: boolean;
  resetProcess: () => void;
}) {
  const isOpen = useDialogStore((state) => state.isDialogOpen(DIALOG_KEY));
  const close = useDialogStore((state) => state.closeDialog);
  const isAuthorized = useAppStore((state) => state.isFullyAuthorized());
  const token = useAppStore((state) => state.exchange?.token);
  const selectedPosition = useSharedTradingStore((state) => state.selectedPosition);
  const accountBalance = useAccountBalance();

  const formatToken = useTokenFormatter(token);
  const [marginAmountInput, setMarginAmountInput] = useState('');

  const { maxAmount, formattedMaxAmount } = useMemo(() => {
    const max = !token || !accountBalance ? undefined : { value: accountBalance, decimals: token.decimals };
    return { maxAmount: max, formattedMaxAmount: formatToken(max?.value ?? 0n, DISPLAY_OPTIONS) };
  }, [accountBalance, token, formatToken]);

  const { error: inputError, isValid } = useTokenAmountValidation({
    inputValue: marginAmountInput,
    token,
    minAmount: 0n,
    maxAmount,
    formattedMinAmount: '0',
  });

  const projection = useMarginProjection({
    selectedPosition,
    marginAmountInput,
    token,
    formatToken,
    displayOptions: DISPLAY_OPTIONS,
  });
  const slider = useMarginAmountSlider({ marginAmountInput, setMarginAmountInput, maxAmount, token });

  const handleDialogClose = () => {
    close(DIALOG_KEY);
    resetProcess();
  };

  useEffect(() => {
    if (isSuccess) {
      handleDialogClose();
    }
  }, [isSuccess, handleDialogClose]);

  useEffect(() => {
    if (isOpen) {
      if (!isAuthorized) {
        handleDialogClose();
      } else {
        setMarginAmountInput('');
      }
    }
  }, [isOpen, isAuthorized]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token || !isValid || marginAmountInput.trim() === '' || !selectedPosition || isPending) return;
    addMargin(decimalStringToBigInt(marginAmountInput, token.decimals), selectedPosition.marketId);
  };

  return (
    <BaseDialog
      dialogKey={DIALOG_KEY}
      anchorTop
      mobileLayout="sheet"
      title={
        <Trans comment="Adjust margin dialog: title including the trading pair">
          Adjust Margin for {{ pair: selectedPosition ? `${selectedPosition.assetCurrency}-USD` : '' }}
        </Trans>
      }
      onSubmit={handleSubmit}
      onClose={handleDialogClose}
      actions={
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isPending || !isValid}
          disableElevation={false}
        >
          <Select
            value={isPending ? 'pending' : inputError.type === 'MAX_AMOUNT' ? 'max' : !isValid ? 'invalid' : 'ok'}
            _pending="Confirming"
            _max="Try lower amount"
            _invalid="Choose amount of margin to add"
            other="Confirm"
            comment="Adjust margin: submit button label across pending / max-amount-error / invalid / ready states"
          />
        </Button>
      }
    >
      <DialogContentText sx={{ mb: 4, mt: 1, color: 'text.primary' }}>
        <Trans comment="Adjust margin dialog: description paragraph">
          Decrease the chance of liquidation by adding more margin.
        </Trans>
      </DialogContentText>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          mb: 1,
          '& .MuiOutlinedInput-input': { pl: 0 },
          // Collapse TokenInputField's empty `helperText=" "` slot; errors still render via `Mui-error`.
          '& .MuiFormHelperText-root:not(.Mui-error)': { marginTop: 0, lineHeight: 0, height: 0, minHeight: 0 },
        }}
      >
        <TokenInputField
          token={token}
          value={marginAmountInput}
          onChange={setMarginAmountInput}
          onMaxClick={slider.handleSetMax}
          disabled={isPending}
          maxAmount={maxAmount}
          errorMessage={inputError.message}
          formattedMaxAmount={formattedMaxAmount}
          inputId="marginAmount"
          showHeader={false}
        />
        <SliderWithInput value={slider.sliderPercent} color="primary" onChange={slider.handleSliderChange} decimalScale={0} />
        <MarginInfoRows selectedPosition={selectedPosition} formattedMaxAmount={formattedMaxAmount} projection={projection} />
      </Box>
    </BaseDialog>
  );
}

function AdjustMarginDialogAPI() {
  const { addMargin, isPending, isSuccess, resetProcess } = usePositionAPI();
  return <AdjustMarginDialogUI addMargin={addMargin} isPending={isPending} isSuccess={isSuccess} resetProcess={resetProcess} />;
}

function AdjustMarginDialogOnChain() {
  const { addMargin, isPending, isSuccess, resetProcess } = useAddMarginOnChain();
  return <AdjustMarginDialogUI addMargin={addMargin} isPending={isPending} isSuccess={isSuccess} resetProcess={resetProcess} />;
}

export function AdjustMarginDialog() {
  const isOneClickEnabled = useIsOneClickEnabled();
  if (isOneClickEnabled) return <AdjustMarginDialogAPI />;
  return <AdjustMarginDialogOnChain />;
}
